import { useState, useEffect, useMemo } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import PostnummerFilter from '../components/PostnummerFilter'
import SearchBar from '../components/SearchBar'
import ExportButtons from '../components/ExportButtons'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import { Users, Calendar, Car, ChevronRight, CreditCard, Banknote, ChevronsUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { ColumnDefinition } from '../utils/exportToCsv'
import QueryExplanationBox, { buildExplanationText } from '../components/QueryExplanationBox'
import { formatNorwegianDate } from '../utils/dateUtils'
import { useMaxLoanDate } from '../hooks/useMaxDataDate'
import AgeWeeklyChart from '../components/AgeWeeklyChart'
import MasterModelGrid from '../components/MasterModelGrid'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { useMirrorSharedDates } from '../hooks/useMirrorSharedDates'
import { localizeApiDetail } from '../i18n/apiError'

// -- Types --

interface PeriodInfo {
  label: string
  start_date: string
  end_date: string
}

interface PeriodsInfo {
  current: PeriodInfo
  previous: PeriodInfo
  ytd_current: PeriodInfo | null
  ytd_previous: PeriodInfo | null
}

interface AgeGroupOverviewFiltered {
  age_group: string
  total_count: number
  market_share: number
  ev_count: number
  ev_percentage: number
  leasing_count: number
  leasing_percentage: number
  top_5_brands: Array<{ brand: string; count: number; percentage: number }>
}

interface AvgAgeByBrandRow {
  brand: string
  current_count: number
  current_avg_age: number | null
  previous_count: number
  previous_avg_age: number | null
}

type TabKey = 'totalt' | 'generelt' | 'leasing' | 'loan'
type OutputMode = 'year_to_date' | 'month_to_date' | 'last_month' | 'last_week' | 'last_year' | 'custom'

// -- Constants --

// Tab labels are translated at render: t(`overview.tabs.${tab}`)
const TAB_CONFIG: Record<TabKey, { icon: typeof Car; category: string }> = {
  totalt: { icon: Users, category: 'all' },
  generelt: { icon: Car, category: 'all' },
  leasing: { icon: CreditCard, category: 'leasing' },
  loan: { icon: Banknote, category: 'loan' },
}

// Labels are translated at render: t(`overview.outputModes.${mode}`)
const OUTPUT_MODES: OutputMode[] = ['year_to_date', 'month_to_date', 'last_month', 'last_week', 'last_year', 'custom']

// Known age groups get a "{{range}} år" label; anything else shows as-is
const AGE_GROUPS = new Set(['18-29', '30-39', '40-49', '50-59', '60-69', '70-79', '80+'])

const AGE_GROUP_COLORS = [
  'rgba(59, 130, 246, 0.8)',
  'rgba(16, 185, 129, 0.8)',
  'rgba(245, 158, 11, 0.8)',
  'rgba(239, 68, 68, 0.8)',
  'rgba(168, 85, 247, 0.8)',
  'rgba(236, 72, 153, 0.8)',
  'rgba(107, 114, 128, 0.8)',
]

const toNumber = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined) return 0
  return typeof v === 'string' ? parseFloat(v) : v
}

// -- Component --

export default function AgeAnalytics() {
  const { t } = useTranslation('age')
  const navigate = useNavigate()
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()
  const maxLoanDate = useMaxLoanDate()

  // UI state
  const [activeTab, setActiveTab] = useState<TabKey>('totalt')
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [customStartDate, setCustomStartDate] = usePersistedState('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState('bp:filter:customEndDate', '')
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('age-analytics:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)
  const [gridView, setGridView] = useState<'merke' | 'mastermodell'>('merke')
  const [maxDataDate, setMaxDataDate] = useState<string | null>(null)

  // Share the chosen period with the useYearDates pages (Leasing, Lån, …)
  useMirrorSharedDates(outputMode, customStartDate, customEndDate, maxDataDate)

  // Weekly age chart state
  const [ageWeeklyData, setAgeWeeklyData] = useState<any[]>([])
  const [prevYearAgeWeeklyData, setPrevYearAgeWeeklyData] = useState<any[]>([])
  const [ageWeeklyLoading, setAgeWeeklyLoading] = useState(false)

  // Fetch actual max SVV data date once on mount
  useEffect(() => {
    publicAPI.getDataFreshness()
      .then((response: any) => {
        const sources: any[] = response.data?.data_sources ?? []
        const svvSource = sources.find((s: any) =>
          typeof s.source_name === 'string' && s.source_name.includes('SVV')
        )
        if (svvSource?.latest_data_date) {
          const raw = svvSource.latest_data_date as string
          const dateStr = raw.substring(0, 10)
          const today = new Date().toISOString().substring(0, 10)
          setMaxDataDate(dateStr <= today ? dateStr : today)
        }
      })
      .catch(() => { /* fall back to backend-reported end date */ })
     
  }, [])

  // Display end date helpers — now handled by QueryExplanationBox
  // const getDisplayEndDate = (backendEndDate: string) => { ... }
  // const getDisplayPreviousEndDate = (backendEndDate: string) => { ... }

  // Data state - overview cards
  const [overviewLoading, setOverviewLoading] = useState(true)
  const [overviewError, setOverviewError] = useState<string | null>(null)
  const [overview, setOverview] = useState<AgeGroupOverviewFiltered[]>([])
  const [allOverview, setAllOverview] = useState<AgeGroupOverviewFiltered[]>([])
  const [periods, setPeriods] = useState<PeriodsInfo | null>(null)

  // Queried-state tracking — only update when API fetch actually runs
  const [queriedOutputMode, setQueriedOutputMode] = useState(outputMode)
  const [queriedVehicleFilter, setQueriedVehicleFilter] = useState(vehicleFilter)
  const [queriedPostnummer, setQueriedPostnummer] = useState(minePostnummer)
  const [queriedActiveTab, setQueriedActiveTab] = useState<TabKey>(activeTab)

  // Data state - avg-age grid
  const [gridLoading, setGridLoading] = useState(true)
  const [gridError, setGridError] = useState<string | null>(null)
  const [gridData, setGridData] = useState<AvgAgeByBrandRow[]>([])
  const [gridSummary, setGridSummary] = useState<AvgAgeByBrandRow | null>(null)
  const [gridSearch, setGridSearch] = useState('')
  const [gridSortConfig, setGridSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  // -- Helpers --

  const getCommonParams = () => {
    // Snapshot queried state
    setQueriedOutputMode(outputMode)
    setQueriedVehicleFilter(vehicleFilter)
    setQueriedPostnummer(minePostnummer)
    setQueriedActiveTab(activeTab)

    const varebilParam = getVarebilParam()
    const params: Record<string, any> = {
      output_mode: outputMode,
      category: TAB_CONFIG[activeTab].category,
    }
    if (varebilParam !== undefined) params.varebil = varebilParam
    if (minePostnummer) params.mine_postnummer = minePostnummer
    if (outputMode === 'custom' && customStartDate && customEndDate) {
      params.custom_start_date = customStartDate
      params.custom_end_date = customEndDate
    }
    return params
  }

  // -- Data fetching --

  const fetchOverview = async () => {
    try {
      setOverviewLoading(true)
      setOverviewError(null)
      const params = getCommonParams()

      // Leasing and Lån tabs need a separate category=all call for their share denominator.
      // Totalt and Generelt use category=all themselves, so no extra call.
      const needsAllCall = params.category !== 'all'
      const allParams = { ...params, category: 'all' }
      const [filteredRes, allRes] = await Promise.all([
        publicAPI.getAgeGroupsOverviewFiltered(params),
        needsAllCall
          ? publicAPI.getAgeGroupsOverviewFiltered(allParams)
          : Promise.resolve(null),
      ])

      const filteredData: AgeGroupOverviewFiltered[] = filteredRes.data.data || []
      setOverview(filteredData)
      if (allRes) {
        setAllOverview(allRes.data.data || [])
      } else {
        setAllOverview(filteredData)
      }
      if (filteredRes.data.periods) setPeriods(filteredRes.data.periods)
    } catch (err: any) {
      setOverviewError(localizeApiDetail(err.response?.data?.detail) || err.message || t('overview.loadAgeError'))
    } finally {
      setOverviewLoading(false)
    }
  }

  const fetchGrid = async () => {
    try {
      setGridLoading(true)
      setGridError(null)
      const params = { ...getCommonParams(), limit: 100 }
      const res = await publicAPI.getAgeAvgByBrand(params)
      const allData = res.data.data || []
      const [summaryRow, ...rows] = allData
      setGridSummary(summaryRow || null)
      setGridData(rows)
      if (res.data.periods) setPeriods(res.data.periods)
    } catch (err: any) {
      setGridError(localizeApiDetail(err.response?.data?.detail) || err.message || t('overview.loadBrandError'))
    } finally {
      setGridLoading(false)
    }
  }

  const fetchAgeWeekly = async () => {
    try {
      setAgeWeeklyLoading(true)
      const currentYear = new Date().getFullYear()
      const varebilParam = getVarebilParam()
      const params: any = { year: currentYear }
      if (varebilParam !== undefined) params.varebil = varebilParam
      const res = await publicAPI.getAgeWeeklyAvgAge(params)
      setAgeWeeklyData(res.data.data || [])
      setPrevYearAgeWeeklyData(res.data.prev_year_data || [])
    } catch (err) {
      console.error('Failed to fetch age weekly data', err)
    } finally {
      setAgeWeeklyLoading(false)
    }
  }

  const fetchAll = () => {
    if (activeTab === 'totalt') {
      fetchAgeWeekly()
    } else {
      fetchOverview()
      fetchGrid()
    }
  }

  // Initial load + re-fetch on tab/mode/filter change
  useEffect(() => {
    if (outputMode === 'custom') return
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, vehicleFilter, outputMode])

  const handleApply = () => {
    if (outputMode === 'custom' && (!customStartDate || !customEndDate)) return
    fetchAll()
  }

  // -- Sorting --

  const handleSort = (key: string) => {
    if (gridSortConfig?.key === key) {
      if (gridSortConfig.direction === 'asc') setGridSortConfig({ key, direction: 'desc' })
      else setGridSortConfig(null)
    } else {
      setGridSortConfig({ key, direction: 'asc' })
    }
  }

  const getSortIcon = (columnKey: string) => {
    if (gridSortConfig?.key === columnKey) {
      return gridSortConfig.direction === 'asc'
        ? <ArrowUp className="w-4 h-4 inline ml-1" />
        : <ArrowDown className="w-4 h-4 inline ml-1" />
    }
    return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  }

  // -- Computed data --

  const filteredGrid = useMemo(() => {
    if (!gridSearch) return gridData
    return gridData.filter(r => r.brand.toLowerCase().includes(gridSearch.toLowerCase()))
  }, [gridData, gridSearch])

  const sortedGrid = useMemo(() => {
    if (!gridSortConfig) return filteredGrid
    return [...filteredGrid].sort((a, b) => {
      let aVal: any, bVal: any
      switch (gridSortConfig.key) {
        case 'brand':
          aVal = a.brand.toLowerCase()
          bVal = b.brand.toLowerCase()
          return gridSortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
        default:
          aVal = toNumber((a as any)[gridSortConfig.key])
          bVal = toNumber((b as any)[gridSortConfig.key])
          return gridSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
      }
    })
  }, [filteredGrid, gridSortConfig])

  const exportColumns: ColumnDefinition[] = [
    { key: 'rank', label: t('overview.export.rank') },
    { key: 'brand', label: t('overview.export.brand') },
    { key: 'current_count', label: periods?.current.label || t('overview.export.currentCount'), format: (v) => toNumber(v).toLocaleString('nb-NO') },
    { key: 'current_avg_age', label: t('overview.export.currentAvgAge'), format: (v) => v !== null ? toNumber(v).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-' },
    { key: 'previous_count', label: periods?.previous.label || t('overview.export.previousCount'), format: (v) => toNumber(v).toLocaleString('nb-NO') },
    { key: 'previous_avg_age', label: t('overview.export.previousAvgAge'), format: (v) => v !== null ? toNumber(v).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-' },
  ]

  // -- Render helpers --

  const ChangeIndicator = ({ current, previous }: { current: number | null; previous: number | null }) => {
    if (current === null || previous === null) return <span className="text-gray-400">-</span>
    const diff = current - previous
    if (Math.abs(diff) < 0.05) return <span className="text-gray-500">0,0</span>
    const colorClass = diff > 0
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-600 dark:text-red-400'
    return (
      <span className={colorClass}>
        {diff.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
      </span>
    )
  }

  // -- Main render --

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('overview.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('overview.subtitle')}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('overview.source')}</p>
      </div>

      {/* Period Selector — hidden on totalt tab */}
      {activeTab !== 'totalt' && (
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('overview.period')}</span>
          <div className="flex flex-wrap gap-2">
            {OUTPUT_MODES.map(mode => (
              <button
                key={mode}
                onClick={() => {
                  setOutputMode(mode)
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  outputMode === mode
                    ? 'bg-primary-600 text-white shadow-xs'
                    : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border'
                }`}
              >
                {t(`overview.outputModes.${mode}` as const)}
              </button>
            ))}
          </div>
          {outputMode === 'custom' && (
            <div className="flex items-end gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('overview.startDate')}
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={e => {
                    const val = e.target.value
                    setCustomStartDate(maxDataDate && val > maxDataDate ? maxDataDate : val)
                  }}
                  max={maxDataDate || undefined}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('overview.endDate')}
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={e => {
                    const val = e.target.value
                    setCustomEndDate(maxDataDate && val > maxDataDate ? maxDataDate : val)
                  }}
                  max={maxDataDate || undefined}
                  className="input-field"
                />
              </div>
            </div>
          )}
          <div className="ml-auto">
            <button
              onClick={handleApply}
              className={`btn-primary text-sm ${outputMode === 'custom' && (!customStartDate || !customEndDate) ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={outputMode === 'custom' && (!customStartDate || !customEndDate)}
            >
              {t('overview.apply')}
            </button>
          </div>
        </div>

        {/* Postal Code Filter */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        </div>
      </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-dark-border">
        {(Object.keys(TAB_CONFIG) as TabKey[]).map(tab => {
          const { icon: Icon } = TAB_CONFIG[tab]
          return (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); setGridView('merke') }}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <span className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                {t(`overview.tabs.${tab}` as const)}
              </span>
            </button>
          )
        })}
      </div>

      {/* Query Explanation Box — replaces old period info + vehicle type boxes */}
      <QueryExplanationBox
        periods={periods}
        maxDataDate={queriedActiveTab === 'loan' ? maxLoanDate : maxDataDate}
        outputMode={queriedOutputMode}
        vehicleFilter={queriedVehicleFilter}
        postnummerFilter={queriedPostnummer}
        activeTab={queriedActiveTab}
        maxDataDateLabel={queriedActiveTab === 'loan' ? t('overview.maxLoanDataLabel') : undefined}
        extraWarnings={[
          ...(queriedActiveTab === 'loan' ? [t('overview.warnings.loan', { date: maxLoanDate ? formatNorwegianDate(maxLoanDate) : t('overview.unknown') })] : []),
          ...(queriedActiveTab === 'leasing' ? [t('overview.warnings.leasing')] : []),
          ...(queriedActiveTab === 'generelt' ? [t('overview.warnings.generelt')] : []),
        ]}
      />

      {activeTab === 'loan' && outputMode === 'last_week' && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <p className="text-sm text-orange-800 dark:text-orange-200">
            <Trans t={t} i18nKey="overview.lastWeekLoanWarning" components={{ b: <strong /> }} />
          </p>
        </div>
      )}

      {/* Totalt tab — Weekly Age Chart */}
      {activeTab === 'totalt' && (
        ageWeeklyLoading ? (
          <LoadingSpinner />
        ) : (
          <AgeWeeklyChart
            data={ageWeeklyData}
            prevYearData={prevYearAgeWeeklyData}
            year={new Date().getFullYear()}
          />
        )
      )}

      {/* Overview Cards */}
      {activeTab !== 'totalt' && (
      overviewLoading ? (
        <LoadingSpinner />
      ) : overviewError ? (
        <ErrorMessage message={overviewError} />
      ) : overview.length === 0 ? (
        <div className="card p-6">
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
            {activeTab === 'loan'
              ? t('overview.noLoanData', { date: maxLoanDate ? formatNorwegianDate(maxLoanDate) : t('overview.unknown') })
              : t('overview.noData')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {overview.map((ag, index) => {
            const allAg = allOverview.find(a => a.age_group === ag.age_group)
            const totalBiler = allAg ? allAg.total_count : ag.total_count
            return (
            <div key={ag.age_group} className="card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: AGE_GROUP_COLORS[index]?.replace('0.8', '0.2') }}
                >
                  <Users className="w-6 h-6" style={{ color: AGE_GROUP_COLORS[index] }} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {AGE_GROUPS.has(ag.age_group) ? t('overview.ageGroupYears', { range: ag.age_group }) : ag.age_group}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('overview.marketShare', { value: ag.market_share.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                {activeTab === 'loan' || activeTab === 'leasing' ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('overview.carCount')}</span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {totalBiler.toLocaleString('nb-NO')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{activeTab === 'leasing' ? t('overview.leasingCount') : t('overview.loanCount')}</span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {ag.total_count.toLocaleString('nb-NO')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{activeTab === 'leasing' ? t('overview.leasingShare') : t('overview.loanShare')}</span>
                      <span className="font-semibold text-blue-600 dark:text-blue-400">
                        {totalBiler > 0
                          ? (ag.total_count / totalBiler * 100).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
                          : '—'} %
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('overview.registrations')}</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {ag.total_count.toLocaleString('nb-NO')}
                    </span>
                  </div>
                )}
              </div>

              {/* Top 5 Brands - 3-column mini-grid */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('overview.top5Brands')}</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 dark:text-gray-400">
                      <th className="text-left font-medium pb-1">{t('overview.colBrand')}</th>
                      <th className="text-right font-medium pb-1">{t('overview.colCount')}</th>
                      <th className="text-right font-medium pb-1">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ag.top_5_brands.map((b, idx) => (
                      <tr key={idx} className="text-gray-600 dark:text-gray-400">
                        <td className="py-0.5">{b.brand}</td>
                        <td className="text-right font-medium">{b.count.toLocaleString('nb-NO')}</td>
                        <td className="text-right">{b.percentage.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )})}

          {/* 8th Summary Card — all age groups combined */}
          {overview.length > 0 && (() => {
            const totalCount = overview.reduce((sum, ag) => sum + ag.total_count, 0)
            const totalBilerAll = allOverview.reduce((sum, ag) => sum + ag.total_count, 0)
            // Aggregate top brands across all age groups
            const brandMap = new Map<string, { count: number }>()
            overview.forEach(ag => {
              ag.top_5_brands.forEach(b => {
                const existing = brandMap.get(b.brand)
                if (existing) existing.count += b.count
                else brandMap.set(b.brand, { count: b.count })
              })
            })
            const topBrands = Array.from(brandMap.entries())
              .map(([brand, data]) => ({ brand, count: data.count, percentage: totalCount > 0 ? data.count / totalCount * 100 : 0 }))
              .sort((a, b) => b.count - a.count)
              .slice(0, 5)

            return (
              <div className="card p-6 border-2 border-primary-300 dark:border-primary-600 mt-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-primary-100 dark:bg-primary-900/30">
                    <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('overview.allAgesTitle')}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('overview.allAgesSubtitle')}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {activeTab === 'generelt' ? (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('overview.registrations')}</span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {totalCount.toLocaleString('nb-NO')}
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('overview.carCount')}</span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {totalBilerAll.toLocaleString('nb-NO')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{activeTab === 'leasing' ? t('overview.leasingCount') : t('overview.loanCount')}</span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {totalCount.toLocaleString('nb-NO')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{activeTab === 'leasing' ? t('overview.leasingShare') : t('overview.loanShare')}</span>
                        <span className="font-semibold text-blue-600 dark:text-blue-400">
                          {totalBilerAll > 0 ? (totalCount / totalBilerAll * 100).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—'} %
                        </span>
                      </div>
                    </>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('overview.top5BrandsAll')}</p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 dark:text-gray-400">
                        <th className="text-left font-medium pb-1">{t('overview.colBrand')}</th>
                        <th className="text-right font-medium pb-1">{t('overview.colCount')}</th>
                        <th className="text-right font-medium pb-1">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topBrands.map((b, idx) => (
                        <tr key={idx} className="text-gray-600 dark:text-gray-400">
                          <td className="py-0.5">{b.brand}</td>
                          <td className="text-right font-medium">{b.count.toLocaleString('nb-NO')}</td>
                          <td className="text-right">{b.percentage.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })()}

          {/* Disclaimer for Alle personkunder tab */}
          {activeTab === 'generelt' && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 whitespace-nowrap">{t('overview.under18Disclaimer')}</p>
          )}
        </div>
      )
      )}

      {/* Sub-tab: Merke / Modell */}
      {activeTab !== 'totalt' && (
        <div className="flex gap-2 border-b border-gray-200 dark:border-dark-border mb-4">
          <button
            onClick={() => setGridView('merke')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              gridView === 'merke'
                ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            {t('overview.gridTabs.merke')}
          </button>
          <button
            onClick={() => setGridView('mastermodell')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              gridView === 'mastermodell'
                ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            {t('overview.gridTabs.mastermodell')}
          </button>
        </div>
      )}

      {/* Average Age per Brand Grid */}
      {activeTab !== 'totalt' && gridView === 'merke' && (
      <div className="card p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {t('overview.gridTitle')}
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
              ({t(`overview.tabs.${activeTab}` as const)})
            </span>
          </h2>
          <div className="flex items-center gap-2">
            {gridSortConfig && (
              <button onClick={() => setGridSortConfig(null)} className="btn-secondary text-sm">
                {t('overview.resetSort')}
              </button>
            )}
            <ExportButtons
              data={sortedGrid.map((row, idx) => ({ rank: idx + 1, ...row }))}
              columns={exportColumns}
              metadata={{
                title: t('overview.export.title', { tab: t(`overview.tabs.${activeTab}` as const), mode: outputMode }),
                dateRange: periods ? t('overview.export.dateRange', { start: formatNorwegianDate(periods.current.start_date), end: formatNorwegianDate(periods.current.end_date) }) : '',
                filters: [
                  vehicleFilter === 'personbil' ? t('overview.export.filterPersonbil') :
                    vehicleFilter === 'varebil' ? t('overview.export.filterVarebil') : '',
                  minePostnummer ? t('overview.export.filterPostnummer', { value: minePostnummer }) : '',
                ].filter(f => f),
                explanationText: buildExplanationText({
                  periods, maxDataDate: queriedActiveTab === 'loan' ? maxLoanDate : maxDataDate, outputMode: queriedOutputMode, vehicleFilter: queriedVehicleFilter,
                  postnummerFilter: queriedPostnummer, activeTab: queriedActiveTab,
                  maxDataDateLabel: queriedActiveTab === 'loan' ? t('overview.maxLoanDataLabel') : undefined,
                  extraWarnings: [
                    ...(queriedActiveTab === 'loan' ? [t('overview.export.warnLoan', { date: maxLoanDate ? formatNorwegianDate(maxLoanDate) : t('overview.unknown') })] : []),
                    ...(queriedActiveTab === 'leasing' ? [t('overview.export.warnLeasing')] : []),
                    ...(queriedActiveTab === 'generelt' ? [t('overview.export.warnGenerelt')] : []),
                  ],
                }),
              }}
              disabled={gridLoading}
            />
          </div>
        </div>
        <div className="mb-4">
          <SearchBar value={gridSearch} onChange={setGridSearch} placeholder={t('overview.searchPlaceholder')} />
        </div>

        {gridLoading ? (
          <LoadingSpinner />
        ) : gridError ? (
          <ErrorMessage message={gridError} />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <th className="text-center w-12">#</th>
                  <th className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('brand')}>
                    {t('overview.cols.brand')} {getSortIcon('brand')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('current_count')}>
                    {periods?.current.label || t('overview.cols.now')} {getSortIcon('current_count')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-r-2 border-gray-300 dark:border-gray-600" onClick={() => handleSort('current_avg_age')}>
                    {t('overview.cols.avgAge')} {getSortIcon('current_avg_age')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('previous_count')}>
                    {periods?.previous.label || t('overview.cols.previous')} {getSortIcon('previous_count')}
                  </th>
                  <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-r-2 border-gray-300 dark:border-gray-600" onClick={() => handleSort('previous_avg_age')}>
                    {t('overview.cols.avgAge')} {getSortIcon('previous_avg_age')}
                  </th>
                  <th className="text-right">{t('overview.cols.ageChange')}</th>
                  <th className="text-center">{t('overview.cols.details')}</th>
                </tr>
              </thead>
              <tbody>
                {/* TOTAL summary row — pinned at top */}
                {gridSummary && (
                  <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                    <td className="text-center py-2">
                      <span className="text-primary-600 dark:text-primary-400">&#8721;</span>
                    </td>
                    <td className="font-bold text-primary-700 dark:text-primary-300 py-2">TOTAL</td>
                    <td className="text-right font-bold py-2">{toNumber(gridSummary.current_count).toLocaleString('nb-NO')}</td>
                    <td className="text-right border-r-2 border-gray-300 dark:border-gray-600 py-2">
                      <span className="font-bold text-gray-900 dark:text-gray-100">
                        {gridSummary.current_avg_age !== null ? gridSummary.current_avg_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                      </span>
                    </td>
                    <td className="text-right font-bold py-2">{toNumber(gridSummary.previous_count).toLocaleString('nb-NO')}</td>
                    <td className="text-right border-r-2 border-gray-300 dark:border-gray-600 py-2">
                      <span className="font-bold text-gray-900 dark:text-gray-100">
                        {gridSummary.previous_avg_age !== null ? gridSummary.previous_avg_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                      </span>
                    </td>
                    <td className="text-right font-bold py-2">
                      <ChangeIndicator current={gridSummary.current_avg_age} previous={gridSummary.previous_avg_age} />
                    </td>
                    <td className="text-center py-2">
                      <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">&mdash;</span>
                    </td>
                  </tr>
                )}
                {sortedGrid.map((row, idx) => (
                  <tr
                    key={row.brand}
                    className={`hover:bg-gray-100 dark:hover:bg-dark-border transition-colors cursor-pointer ${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
                    onClick={() => navigate(`/age-analytics/brand/${encodeURIComponent(row.brand)}?tab=${activeTab}&mode=${outputMode}`)}
                  >
                    <td className="text-center text-gray-700 dark:text-gray-300">{idx + 1}</td>
                    <td className="font-medium">{row.brand}</td>
                    <td className="text-right">{toNumber(row.current_count).toLocaleString('nb-NO')}</td>
                    <td className="text-right border-r-2 border-gray-300 dark:border-gray-600">
                      <span className="text-gray-900 dark:text-gray-100">
                        {row.current_avg_age !== null ? row.current_avg_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                      </span>
                    </td>
                    <td className="text-right">{toNumber(row.previous_count).toLocaleString('nb-NO')}</td>
                    <td className="text-right border-r-2 border-gray-300 dark:border-gray-600">
                      <span className="text-gray-900 dark:text-gray-100">
                        {row.previous_avg_age !== null ? row.previous_avg_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                      </span>
                    </td>
                    <td className="text-right">
                      <ChangeIndicator current={row.current_avg_age} previous={row.previous_avg_age} />
                    </td>
                    <td className="text-center">
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/age-analytics/brand/${encodeURIComponent(row.brand)}?tab=${activeTab}&mode=${outputMode}`) }}
                        className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        <span className="text-sm font-medium">{t('overview.cols.details')}</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sortedGrid.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('overview.noData')}</p>
            )}
          </div>
        )}
      </div>
      )}

      {/* Average Age per Master Model Grid */}
      {activeTab !== 'totalt' && gridView === 'mastermodell' && (
        <MasterModelGrid
          outputMode={outputMode}
          category={TAB_CONFIG[activeTab].category}
          varebil={getVarebilParam()}
          minePostnummer={postnummerFilterEnabled && minePostnummer ? minePostnummer : undefined}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          activeTab={activeTab}
        />
      )}
    </div>
  )
}
